import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from "react";
import { clearPortalPinChallenge } from "@/lib/activeLoginSession";

interface OutletData {
  id: string;
  outlet_name: string;
  outlet_code?: string;
  address?: string;
  channel_type?: 'online' | 'offline';
}

interface OutletContext {
  outletId: string;
  outletData: OutletData;
}

interface OutletLoginContextType {
  outletContext: OutletContext | null;
  outletContextLoading: boolean;
  selectOutlet: (outletId: string, outletData: OutletData) => void;
  clearOutletContext: () => void;
  isOutletSelected: boolean;
}

const STORAGE_KEY = 'lysara_outlet_login';

const OutletLoginContext = createContext<OutletLoginContextType>({
  outletContext: null,
  outletContextLoading: true,
  selectOutlet: () => {},
  clearOutletContext: () => {},
  isOutletSelected: false
});

export const OutletLoginProvider = ({ children }: { children: ReactNode }) => {
  const [outletContext, setOutletContext] = useState<OutletContext | null>(null);
  const [outletContextLoading, setOutletContextLoading] = useState(true);

  // Load from localStorage on mount with backward-compatible repair
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        
        // Validate: outletId must exist
        if (!parsed.outletId) {
          console.warn('Invalid outlet context in localStorage (missing outletId), clearing...');
          localStorage.removeItem(STORAGE_KEY);
          return;
        }
        
        // Auto-heal: if outletData.id is missing but outletId exists, populate it
        if (parsed.outletData && !parsed.outletData.id) {
          parsed.outletData.id = parsed.outletId;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
        }
        
        setOutletContext(parsed);
      }
    } catch (error) {
      console.error('Failed to load outlet context from localStorage:', error);
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setOutletContextLoading(false);
    }
  }, []);

  const selectOutlet = (outletId: string, outletData: OutletData) => {
    const context: OutletContext = { outletId, outletData };
    setOutletContext(context);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
  };

  const clearOutletContext = () => {
    setOutletContext(null);
    localStorage.removeItem(STORAGE_KEY);
    clearPortalPinChallenge("outlet");
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    outletContext,
    outletContextLoading,
    selectOutlet,
    clearOutletContext,
    isOutletSelected: !!outletContext
  }), [outletContext, outletContextLoading]);

  return (
    <OutletLoginContext.Provider value={contextValue}>
      {children}
    </OutletLoginContext.Provider>
  );
};

export const useOutletLogin = () => useContext(OutletLoginContext);
